import {
  Color3,
  MaterialPluginBase,
  ShaderLanguage,
  StandardMaterial,
  type AbstractEngine,
  type AbstractMesh,
  type MaterialDefines,
  type Scene,
  type SubMesh,
  type UniformBuffer,
} from '@babylonjs/core';

/**
 * Per-chunk terrain shading: reads the already-sampled landcover color (the material's
 * standard diffuseTexture, exposed to the fragment as `baseColor`) and overlays procedural
 * grass variation, slope-based rock, and elevation-based snow on top. Reading `baseColor`
 * rather than a custom sampler avoids the plugin-sampler binding (which read back white here).
 */
export class TerrainShadingPlugin extends MaterialPluginBase {
  enabled: boolean;
  // Darker stone gray — replaces the "light brown" that washed out the previous look.
  rockColor = new Color3(126 / 255, 116 / 255, 104 / 255); // #7E7468
  snowColor = new Color3(242 / 255, 240 / 255, 236 / 255);
  /** Y world coord where snow starts/finishes. */
  snowStart = 1500;
  snowEnd = 1900;
  /** Slope (1 - n.y) where rock blend starts/finishes. Tuned so only near-vertical
   *  cliffs turn rocky; gentle hills stay grass. */
  slopeStart = 0.75;
  slopeEnd = 0.95;
  /** Strength of the procedural grass mottling applied to green cover (0 = off). */
  grassDetail = 1.0;

  constructor(material: StandardMaterial) {
    super(material, 'TerrainShading', 250, { TERRAIN_SHADING: false });
    this.enabled = false;
  }

  override isCompatible(_shaderLanguage: ShaderLanguage): boolean {
    return true;
  }

  override prepareDefines(defines: MaterialDefines, _scene: Scene, _mesh: AbstractMesh): void {
    (defines as unknown as Record<string, boolean>).TERRAIN_SHADING = this.enabled;
  }

  override getUniforms(_shaderLanguage?: ShaderLanguage): {
    ubo?: Array<{ name: string; size?: number; type?: string }>;
    vertex?: string;
    fragment?: string;
  } {
    return {
      ubo: [
        { name: 'terrainRockColor', size: 3, type: 'vec3' },
        { name: 'terrainSnowColor', size: 3, type: 'vec3' },
        { name: 'terrainSnowStart', size: 1, type: 'float' },
        { name: 'terrainSnowEnd', size: 1, type: 'float' },
        { name: 'terrainSlopeStart', size: 1, type: 'float' },
        { name: 'terrainSlopeEnd', size: 1, type: 'float' },
        { name: 'terrainGrassDetail', size: 1, type: 'float' },
      ],
    };
  }

  override bindForSubMesh(
    uniformBuffer: UniformBuffer,
    _scene: Scene,
    _engine: AbstractEngine,
    _subMesh: SubMesh,
  ): void {
    if (!this.enabled) return;
    uniformBuffer.updateColor3('terrainRockColor', this.rockColor);
    uniformBuffer.updateColor3('terrainSnowColor', this.snowColor);
    uniformBuffer.updateFloat('terrainSnowStart', this.snowStart);
    uniformBuffer.updateFloat('terrainSnowEnd', this.snowEnd);
    uniformBuffer.updateFloat('terrainSlopeStart', this.slopeStart);
    uniformBuffer.updateFloat('terrainSlopeEnd', this.slopeEnd);
    uniformBuffer.updateFloat('terrainGrassDetail', this.grassDetail);
  }

  override getCustomCode(
    shaderType: string,
    shaderLanguage?: ShaderLanguage,
  ): { [pointName: string]: string } | null {
    if (shaderLanguage === ShaderLanguage.WGSL) {
      if (shaderType === 'vertex') return {};
      return {
        CUSTOM_FRAGMENT_DEFINITIONS: `
          fn terrainHash(p: vec2<f32>) -> f32 {
            return fract(sin(dot(p, vec2<f32>(127.1, 311.7))) * 43758.5453);
          }
          fn terrainNoise(p: vec2<f32>) -> f32 {
            let i = floor(p);
            let f = fract(p);
            let u = f * f * (3.0 - 2.0 * f);
            let a = terrainHash(i);
            let b = terrainHash(i + vec2<f32>(1.0, 0.0));
            let c = terrainHash(i + vec2<f32>(0.0, 1.0));
            let d = terrainHash(i + vec2<f32>(1.0, 1.0));
            return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
          }
        `,
        CUSTOM_FRAGMENT_UPDATE_DIFFUSE: `
          #ifdef TERRAIN_SHADING
            var coverColor = baseColor.rgb;
            // Multi-scale variation so far grass stretches read as a living meadow, not flat paint.
            // Gated on green dominance so roads/sand/rock/water stay clean.
            let grassiness = smoothstep(0.0, 0.08, coverColor.g - max(coverColor.r, coverColor.b));
            let pw = fragmentInputs.vPositionW.xz;
            let macro = terrainNoise(pw * 0.025); // ~40 m field-scale patches
            let mid = terrainNoise(pw * 0.18);    // ~5.5 m mottle
            let fine = terrainNoise(pw * 0.9);    // ~1.1 m detail
            let mott = (mid - 0.5) * 1.0 + (fine - 0.5) * 0.6;
            let grassColor = coverColor * (1.0 + mott * 0.07 * uniforms.terrainGrassDetail);
            // Macro patches: lusher (deeper, cooler green) in some areas, faintly drier (warmer) in
            // a few others. Green is the default everywhere; the dry tint is deliberately weak and
            // late-onset (only macro > 0.7) so the world stays green and the warm drift is a rare,
            // gentle accent rather than a competing color. Hue-driven so the cel posterization
            // (which bands luminance but preserves chroma) doesn't crush it.
            let lush = grassColor * vec3<f32>(0.92, 1.04, 0.85);
            let dry = grassColor * vec3<f32>(1.08, 1.04, 0.86);
            let lushT = smoothstep(0.40, 0.0, macro) * 0.35 * uniforms.terrainGrassDetail;
            let dryT = smoothstep(0.70, 1.0, macro) * 0.22 * uniforms.terrainGrassDetail;
            var varied = mix(grassColor, lush, lushT);
            varied = mix(varied, dry, dryT);
            coverColor = mix(coverColor, varied, grassiness);
            let slope = clamp(1.0 - normalize(fragmentInputs.vNormalW).y, 0.0, 1.0);
            let rockMix = smoothstep(uniforms.terrainSlopeStart, uniforms.terrainSlopeEnd, slope);
            var terrainColor = mix(coverColor, uniforms.terrainRockColor, rockMix);
            let snowMix = smoothstep(uniforms.terrainSnowStart, uniforms.terrainSnowEnd, fragmentInputs.vPositionW.y);
            terrainColor = mix(terrainColor, uniforms.terrainSnowColor, snowMix);
            baseColor = vec4<f32>(terrainColor, baseColor.a);
          #endif
        `,
      };
    }

    // GLSL (WebGL2)
    if (shaderType === 'vertex') return {};
    return {
      CUSTOM_FRAGMENT_DEFINITIONS: `
        float terrainHash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }
        float terrainNoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          float a = terrainHash(i);
          float b = terrainHash(i + vec2(1.0, 0.0));
          float c = terrainHash(i + vec2(0.0, 1.0));
          float d = terrainHash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
        }
      `,
      CUSTOM_FRAGMENT_UPDATE_DIFFUSE: `
        #ifdef TERRAIN_SHADING
          vec3 coverColor = baseColor.rgb;
          // Multi-scale variation so far grass stretches read as a living meadow, not flat paint.
          // Gated on green dominance so roads/sand/rock/water stay clean.
          float grassiness = smoothstep(0.0, 0.08, coverColor.g - max(coverColor.r, coverColor.b));
          vec2 pw = vPositionW.xz;
          float macro = terrainNoise(pw * 0.025); // ~40 m field-scale patches
          float mid = terrainNoise(pw * 0.18);    // ~5.5 m mottle
          float fine = terrainNoise(pw * 0.9);    // ~1.1 m detail
          float mott = (mid - 0.5) * 1.0 + (fine - 0.5) * 0.6;
          vec3 grassColor = coverColor * (1.0 + mott * 0.07 * terrainGrassDetail);
          // Macro patches: lusher (deeper, cooler green) in some areas, faintly drier (warmer) in
          // a few others. Green is the default everywhere; the dry tint is deliberately weak and
          // late-onset (only macro > 0.7) so the world stays green and the warm drift is a rare,
          // gentle accent rather than a competing color. Hue-driven so the cel posterization
          // (which bands luminance but preserves chroma) doesn't crush it.
          vec3 lush = grassColor * vec3(0.92, 1.04, 0.85);
          vec3 dry = grassColor * vec3(1.08, 1.04, 0.86);
          float lushT = smoothstep(0.40, 0.0, macro) * 0.35 * terrainGrassDetail;
          float dryT = smoothstep(0.70, 1.0, macro) * 0.22 * terrainGrassDetail;
          vec3 varied = mix(grassColor, lush, lushT);
          varied = mix(varied, dry, dryT);
          coverColor = mix(coverColor, varied, grassiness);
          float slope = clamp(1.0 - normalize(vNormalW).y, 0.0, 1.0);
          float rockMix = smoothstep(terrainSlopeStart, terrainSlopeEnd, slope);
          vec3 terrainColor = mix(coverColor, terrainRockColor, rockMix);
          float snowMix = smoothstep(terrainSnowStart, terrainSnowEnd, vPositionW.y);
          terrainColor = mix(terrainColor, terrainSnowColor, snowMix);
          baseColor.rgb = terrainColor;
        #endif
      `,
    };
  }
}
